import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { MdStar } from "react-icons/md";
import { TbLogin2, TbLogout2, TbMapPin } from "react-icons/tb";
import type { BookingDetailResult } from "src/store/apis/Hotels";
import { BOOKING_STATUS_BADGES, parseStarRating } from "./constants";

const TimelineEdge: FC<{ icon: ReactNode; title: ReactNode; date: ReactNode; end?: boolean }> = ({ icon, title, date, end }) => {
  return (
    <div className={`flex items-center gap-3 ${end ? "flex-row-reverse text-end" : ""}`}>
      <div className="bg-background-primary-lighter text-font-active flex size-9 shrink-0 items-center justify-center rounded-md text-lg">{icon}</div>
      <div className="flex flex-col">
        <div className="text-font-subtle text-xs uppercase">{title}</div>
        <div className="text-font-default text-base font-semibold">{date}</div>
      </div>
    </div>
  );
};

const BookingSummary: FC<{ bookingDetail: BookingDetailResult }> = ({ bookingDetail }) => {
  const { t } = useTranslation();
  const { hotelDetails } = bookingDetail;

  const statusBadge = BOOKING_STATUS_BADGES[bookingDetail?.orderStatus as keyof typeof BOOKING_STATUS_BADGES];
  const starCount = parseStarRating(hotelDetails.rating);
  const [latitude, longitude] = hotelDetails.map?.split("|") ?? [];
  const mapUrl = latitude && longitude ? `https://www.google.com/maps?q=${latitude},${longitude}` : null;

  const checkIn = dayjs(bookingDetail.checkIn);
  const checkOut = dayjs(bookingDetail.checkOut);
  const nights = checkOut.startOf("day").diff(checkIn.startOf("day"), "day");

  return (
    <div className="bg-background-default border-border-subtle flex w-full flex-col overflow-hidden rounded-xs border shadow-xs">
      <div className="bg-background-subtle border-border-subtle flex items-center justify-between gap-4 border-b px-4 py-3">
        <div className="text-font-default text-base font-semibold">{t("BookingResult.summary.title")}</div>
        {statusBadge && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-font-subtle">{t("BookingResult.summary.bookingStatus")}</span>
            <span className={`rounded-pill px-3 py-1 font-medium ${statusBadge.className}`}>{t(statusBadge.label)}</span>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-8 p-4">
        <div className="flex items-center gap-4">
          {hotelDetails.image && (
            <img src={hotelDetails.image} alt={hotelDetails.hotelName ?? ""} className="aspect-video w-full max-w-50 shrink-0 rounded-md object-cover" />
          )}
          <div className="flex min-w-0 flex-col gap-1">
            {starCount > 0 && (
              <div className="text-icon-rating-star flex items-center gap-0.5">
                {Array.from({ length: starCount }, (_, i) => (
                  <MdStar key={i} className="size-4" />
                ))}
              </div>
            )}
            <div className="text-font-default truncate text-lg font-semibold">{hotelDetails.hotelName}</div>
            {mapUrl && (
              <a href={mapUrl} target="_blank" rel="noopener noreferrer" className="text-font-active flex w-fit items-center gap-1 text-xs font-medium">
                <TbMapPin className="size-4" />
                {t("BookingResult.summary.viewOnMap")}
              </a>
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-md">
          <div className="flex items-center gap-4 p-4">
            <TimelineEdge
              icon={<TbLogin2 />}
              title={t("BookingResult.summary.checkInTime")}
              date={checkIn.format("MMM D, YYYY")}
            />
            <div className="flex flex-1 items-center">
              <div className="bg-border-subtle h-px flex-1" />
              <span className="bg-background-primary-lighter text-font-active rounded-pill px-3 py-1 text-xs font-medium uppercase">
                {t("BookingResult.stayChips.nights", { count: nights })}
              </span>
              <div className="bg-border-subtle h-px flex-1" />
            </div>
            <TimelineEdge
              end
              icon={<TbLogout2 />}
              title={t("BookingResult.summary.checkOutTime")}
              date={checkOut.format("MMM D, YYYY")}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSummary;
